import { createContext, useContext, useState } from 'react'

import type { BalanceCorrectionDialogProps } from './dialogs/BalanceCorrectionDialog'
import type { ConfirmBalancesDialogProps } from './dialogs/ConfirmBalancesDialog'
import type { DailyCorrectionDialogProps } from './dialogs/DailyCorrectionDialog'
import type { DiscardDraftsDialogProps } from './dialogs/DiscardDraftsDialog'

type Action =
  | { type: 'daily'; params: Omit<DailyCorrectionDialogProps, 'onClose'> }
  | { type: 'balance'; params: Omit<BalanceCorrectionDialogProps, 'onClose'> }
  | { type: 'confirm'; params: Omit<ConfirmBalancesDialogProps, 'onClose'> }
  | { type: 'discard'; params: Omit<DiscardDraftsDialogProps, 'onClose'> }

type ActionParams<T extends Action['type']> = Extract<Action, { type: T }>['params']

/** Own one page-wide dialog workflow; overlapping actions preserve the active review. */
export const useCreateAccruedInterestActionsContext = () => {
  const [actionState, setActionState] = useState<Action | null>(null)
  return {
    actionState,
    openDailyCorrection: (params: ActionParams<'daily'>) =>
      setActionState((current) => current ?? { type: 'daily', params }),
    openBalanceCorrection: (params: ActionParams<'balance'>) =>
      setActionState((current) => current ?? { type: 'balance', params }),
    openConfirmBalances: (params: ActionParams<'confirm'>) =>
      setActionState((current) => current ?? { type: 'confirm', params }),
    openDiscardDrafts: (params: ActionParams<'discard'>) =>
      setActionState((current) => current ?? { type: 'discard', params }),
    close: () => setActionState(null),
  }
}

export const AccruedInterestActionsContext = createContext<ReturnType<
  typeof useCreateAccruedInterestActionsContext
> | null>(null)
export const useAccruedInterestActions = () => {
  const context = useContext(AccruedInterestActionsContext)
  if (!context) throw new Error('useAccruedInterestActions requires AccruedInterestActionsProvider')
  return context
}
